import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiEndpoint } from '../config/api'
import { useTheme } from '../contexts/ThemeContext'

const BookAppointment = () => {
  const navigate = useNavigate()
  const { isNight } = useTheme()
  const [step, setStep] = useState(1) // 1: Select Date/Time, 2: Enter Details, 3: Confirmation
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedTime, setSelectedTime] = useState(null)
  const [availableDates, setAvailableDates] = useState([])
  const [availableTimes, setAvailableTimes] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    notes: '',
  })

  const services = [
    'External Network Penetration Test',
    'External Web Application Penetration Test',
    'External OSINT Assessment',
    'Internal Network Security Controls Assessment',
    'Internal Vulnerability Assessment',
    'General Consultation',
  ]

  // Generate available dates (next 30 days, excluding weekends)
  useEffect(() => {
    const dates = []
    const today = new Date()
    let daysAdded = 0
    let currentDate = new Date(today)

    while (daysAdded < 30) {
      currentDate.setDate(currentDate.getDate() + 1)
      const dayOfWeek = currentDate.getDay()

      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        dates.push(new Date(currentDate))
        daysAdded++
      }
    }

    setAvailableDates(dates)
  }, [])

  // Generate available time slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      // Business hours: 9 AM - 5 PM MT, 1-hour slots
      const times = [
        '09:00 AM',
        '10:00 AM',
        '11:00 AM',
        '12:00 PM',
        '01:00 PM',
        '02:00 PM',
        '03:00 PM',
        '04:00 PM',
      ]
      setAvailableTimes(times)
    }
  }, [selectedDate])

  const handleDateSelect = (date) => {
    setSelectedDate(date)
    setSelectedTime(null)
  }

  const handleTimeSelect = (time) => {
    setSelectedTime(time)
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleNextStep = () => {
    if (step === 1 && selectedDate && selectedTime) {
      setStep(2)
    }
  }

  const handlePrevStep = () => {
    if (step === 2) {
      setStep(1)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // Create appointment
      const appointmentData = {
        date: selectedDate.toISOString().split('T')[0],
        time: selectedTime,
        ...formData,
      }

      const response = await fetch(getApiEndpoint('/appointments/schedule'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointmentData),
      })

      const data = await response.json()

      if (data.success) {
        setStep(3)
      } else {
        setError(data.message || 'Failed to schedule appointment')
      }
    } catch (err) {
      setError('An error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const getMonthDates = (monthOffset = 0) => {
    const today = new Date()
    const targetMonth = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1)

    return availableDates.filter(date =>
      date.getMonth() === targetMonth.getMonth() &&
      date.getFullYear() === targetMonth.getFullYear()
    )
  }

  const [currentMonth, setCurrentMonth] = useState(0)
  const monthDates = getMonthDates(currentMonth)
  const monthName = monthDates.length > 0
    ? monthDates[0].toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

  return (
    <div className={`min-h-screen ${isNight ? 'bg-void' : 'bg-gradient-to-b from-white to-gray-50'}`}>
      {/* Header */}
      <div className={`py-16 ${isNight ? 'bg-gradient-to-r from-void-light/80 to-void-elevated/80 backdrop-blur-sm' : 'glass-hero text-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${isNight ? 'font-orbitron text-cyber-cyan' : ''}`}>
            {isNight ? 'SCHEDULE PROTOCOL' : 'Schedule a Consultation'}
          </h1>
          <p className={isNight ? 'text-xl text-gray-400' : 'text-xl text-primary-100'}>
            {isNight ? 'Initialize secure communication with our team' : 'Book a time to discuss your security needs with our team'}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                step >= 1
                  ? isNight ? 'bg-cyber-cyan text-void' : 'bg-primary-600 text-white'
                  : isNight ? 'bg-void-elevated text-gray-500' : 'bg-gray-200 text-gray-600'
              }`}>
                1
              </div>
              <span className={`ml-3 font-medium ${isNight ? 'text-gray-300' : 'text-gray-900'}`}>
                {isNight ? 'SELECT TIME' : 'Select Date & Time'}
              </span>
            </div>
            <div className={`flex-1 h-1 mx-4 ${isNight ? 'bg-void-elevated' : 'bg-gray-200'}`}>
              <div className={`h-full transition-all ${step >= 2 ? (isNight ? 'bg-cyber-cyan w-full' : 'bg-primary-600 w-full') : 'w-0'}`}></div>
            </div>
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                step >= 2
                  ? isNight ? 'bg-cyber-cyan text-void' : 'bg-primary-600 text-white'
                  : isNight ? 'bg-void-elevated text-gray-500' : 'bg-gray-200 text-gray-600'
              }`}>
                2
              </div>
              <span className={`ml-3 font-medium ${isNight ? 'text-gray-300' : 'text-gray-900'}`}>
                {isNight ? 'DETAILS' : 'Your Details'}
              </span>
            </div>
            <div className={`flex-1 h-1 mx-4 ${isNight ? 'bg-void-elevated' : 'bg-gray-200'}`}>
              <div className={`h-full transition-all ${step >= 3 ? (isNight ? 'bg-cyber-cyan w-full' : 'bg-primary-600 w-full') : 'w-0'}`}></div>
            </div>
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                step >= 3
                  ? isNight ? 'bg-cyber-cyan text-void' : 'bg-primary-600 text-white'
                  : isNight ? 'bg-void-elevated text-gray-500' : 'bg-gray-200 text-gray-600'
              }`}>
                3
              </div>
              <span className={`ml-3 font-medium ${isNight ? 'text-gray-300' : 'text-gray-900'}`}>
                {isNight ? 'CONFIRM' : 'Confirmation'}
              </span>
            </div>
          </div>
        </div>

        {/* Step 1: Date and Time Selection */}
        {step === 1 && (
          <div className={isNight ? 'neon-border-gradient p-6' : 'card'}>
            <h2 className={`text-2xl font-bold mb-6 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
              {isNight ? 'SELECT DATE & TIME' : 'Select Date and Time'}
            </h2>

            {/* Calendar Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setCurrentMonth(Math.max(0, currentMonth - 1))}
                disabled={currentMonth === 0}
                className={`px-4 py-2 rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  isNight
                    ? 'text-cyber-cyan hover:bg-void-elevated'
                    : 'text-primary-600 hover:bg-primary-50'
                }`}
              >
                ← {isNight ? 'PREV' : 'Previous'}
              </button>
              <h3 className={`text-lg font-semibold ${isNight ? 'text-white' : 'text-gray-900'}`}>{monthName}</h3>
              <button
                onClick={() => setCurrentMonth(currentMonth + 1)}
                disabled={monthDates.length === 0}
                className={`px-4 py-2 rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  isNight
                    ? 'text-cyber-cyan hover:bg-void-elevated'
                    : 'text-primary-600 hover:bg-primary-50'
                }`}
              >
                {isNight ? 'NEXT' : 'Next'} →
              </button>
            </div>

            {/* Available Dates */}
            <div className="mb-6">
              <h3 className={`text-sm font-semibold mb-3 ${isNight ? 'text-gray-400' : 'text-gray-700'}`}>
                {isNight ? 'AVAILABLE DATES' : 'Available Dates'}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {monthDates.map((date, index) => (
                  <button
                    key={index}
                    onClick={() => handleDateSelect(date)}
                    className={`p-3 rounded-lg border-2 text-sm transition-all backdrop-blur-sm ${
                      selectedDate?.toDateString() === date.toDateString()
                        ? isNight
                          ? 'border-cyber-cyan bg-cyber-cyan/20 text-cyber-cyan font-semibold'
                          : 'border-primary-600 bg-primary-50/70 text-primary-700 font-semibold'
                        : isNight
                          ? 'border-cyber-cyan/20 bg-void-elevated/50 text-gray-300 hover:border-cyber-cyan/50 hover:bg-void-elevated/70'
                          : 'border-gray-200/50 bg-white/50 hover:border-primary-300 hover:bg-white/70'
                    }`}
                  >
                    {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    <br />
                    <span className={`text-xs ${isNight ? 'text-gray-500' : 'text-gray-500'}`}>
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Available Times */}
            {selectedDate && (
              <div className="mb-6">
                <h3 className={`text-sm font-semibold mb-3 ${isNight ? 'text-gray-400' : 'text-gray-700'}`}>
                  {isNight ? 'AVAILABLE TIMES ON' : 'Available Times on'} {formatDate(selectedDate)}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {availableTimes.map((time, index) => (
                    <button
                      key={index}
                      onClick={() => handleTimeSelect(time)}
                      className={`p-3 rounded-lg border-2 transition-all backdrop-blur-sm ${
                        selectedTime === time
                          ? isNight
                            ? 'border-cyber-magenta bg-cyber-magenta/20 text-cyber-magenta font-semibold'
                            : 'border-primary-600 bg-primary-50/70 text-primary-700 font-semibold'
                          : isNight
                            ? 'border-cyber-cyan/20 bg-void-elevated/50 text-gray-300 hover:border-cyber-magenta/50 hover:bg-void-elevated/70'
                            : 'border-gray-200/50 bg-white/50 hover:border-primary-300 hover:bg-white/70'
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
                <p className={`text-xs mt-3 ${isNight ? 'text-gray-500' : 'text-gray-500'}`}>
                  {isNight ? 'All times in Mountain Time (MT). 60-minute sessions.' : 'All times are in Mountain Time (MT). Sessions are 60 minutes.'}
                </p>
              </div>
            )}

            <button
              onClick={handleNextStep}
              disabled={!selectedDate || !selectedTime}
              className={`w-full disabled:opacity-50 disabled:cursor-not-allowed ${isNight ? 'btn-neon' : 'btn-primary'}`}
            >
              {isNight ? 'CONTINUE' : 'Continue to Details'}
            </button>
          </div>
        )}

        {/* Step 2: Contact Details */}
        {step === 2 && (
          <div className={isNight ? 'neon-border-gradient p-6' : 'card'}>
            <h2 className={`text-2xl font-bold mb-6 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
              {isNight ? 'YOUR INFORMATION' : 'Your Information'}
            </h2>

            <div className={`p-4 rounded-lg mb-6 backdrop-blur-sm ${
              isNight ? 'bg-cyber-cyan/10 border border-cyber-cyan/20' : 'bg-primary-50/70 border border-primary-200/50'
            }`}>
              <p className={`text-sm font-medium ${isNight ? 'text-gray-400' : 'text-primary-900'}`}>
                {isNight ? 'SELECTED TIME:' : 'Selected Time:'}
              </p>
              <p className={`text-lg font-semibold ${isNight ? 'text-cyber-cyan' : 'text-primary-700'}`}>
                {formatDate(selectedDate)} at {selectedTime} MT
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className={`block text-sm font-medium mb-2 ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                  {isNight ? 'FULL NAME *' : 'Full Name *'}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className={isNight ? 'glass-input-night' : 'glass-input'}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className={`block text-sm font-medium mb-2 ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                  {isNight ? 'EMAIL ADDRESS *' : 'Email Address *'}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className={isNight ? 'glass-input-night' : 'glass-input'}
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className={`block text-sm font-medium mb-2 ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                  {isNight ? 'PHONE NUMBER *' : 'Phone Number *'}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={isNight ? 'glass-input-night' : 'glass-input'}
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="company" className={`block text-sm font-medium mb-2 ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                  {isNight ? 'COMPANY' : 'Company'}
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className={isNight ? 'glass-input-night' : 'glass-input'}
                  placeholder="Your Company"
                />
              </div>

              <div>
                <label htmlFor="service" className={`block text-sm font-medium mb-2 ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                  {isNight ? 'SERVICE OF INTEREST *' : 'Service of Interest *'}
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  value={formData.service}
                  onChange={handleInputChange}
                  className={isNight ? 'glass-input-night' : 'glass-input'}
                >
                  <option value="">{isNight ? 'Select a protocol' : 'Select a service'}</option>
                  {services.map((service, index) => (
                    <option key={index} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="notes" className={`block text-sm font-medium mb-2 ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                  {isNight ? 'ADDITIONAL NOTES' : 'Additional Notes'}
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  rows="4"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className={isNight ? 'glass-input-night' : 'glass-input'}
                  placeholder={isNight ? 'Specify any topics for discussion...' : 'Any specific topics you\'d like to discuss...'}
                />
              </div>

              {error && (
                <div className={`p-4 rounded-lg backdrop-blur-sm ${
                  isNight ? 'bg-red-900/40 text-red-400 border border-red-500/30' : 'bg-red-50/80 text-red-800 border border-red-200/50'
                }`}>
                  {error}
                </div>
              )}

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all ${
                    isNight
                      ? 'bg-void-elevated border border-cyber-cyan/20 text-gray-300 hover:border-cyber-cyan/50'
                      : 'bg-white/50 border border-gray-200/50 text-gray-700 hover:bg-white/70'
                  }`}
                >
                  {isNight ? 'BACK' : 'Back'}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 disabled:opacity-50 disabled:cursor-not-allowed ${isNight ? 'btn-neon' : 'btn-primary'}`}
                >
                  {loading ? (isNight ? 'SCHEDULING...' : 'Scheduling...') : (isNight ? 'CONFIRM' : 'Confirm Appointment')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <div className={`text-center ${isNight ? 'neon-border-gradient p-6' : 'card'}`}>
            <div className="mb-6">
              <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                isNight ? 'bg-cyber-cyan/20 border border-cyber-cyan/50' : 'bg-green-100'
              }`}>
                <svg className={`w-8 h-8 ${isNight ? 'text-cyber-cyan' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className={`text-3xl font-bold mb-2 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
                {isNight ? 'APPOINTMENT CONFIRMED' : 'Appointment Confirmed!'}
              </h2>
              <p className={isNight ? 'text-gray-400' : 'text-gray-600'}>
                {isNight ? 'Consultation scheduled successfully.' : 'Your consultation has been scheduled successfully.'}
              </p>
            </div>

            <div className={`p-6 rounded-lg mb-6 text-left backdrop-blur-sm ${
              isNight ? 'bg-void-elevated/50 border border-cyber-cyan/20' : 'bg-gray-50/70 border border-gray-200/50'
            }`}>
              <h3 className={`font-semibold mb-4 ${isNight ? 'text-white' : 'text-gray-900'}`}>
                {isNight ? 'APPOINTMENT DETAILS:' : 'Appointment Details:'}
              </h3>
              <div className={`space-y-2 text-sm ${isNight ? 'text-gray-400' : 'text-gray-700'}`}>
                <p><span className={`font-medium ${isNight ? 'text-gray-300' : ''}`}>Date:</span> {formatDate(selectedDate)}</p>
                <p><span className={`font-medium ${isNight ? 'text-gray-300' : ''}`}>Time:</span> {selectedTime} MT</p>
                <p><span className={`font-medium ${isNight ? 'text-gray-300' : ''}`}>Duration:</span> 60 minutes</p>
                <p><span className={`font-medium ${isNight ? 'text-gray-300' : ''}`}>Service:</span> {formData.service}</p>
              </div>
            </div>

            <div className={`p-4 rounded-lg mb-6 backdrop-blur-sm ${
              isNight ? 'bg-cyber-cyan/10 border border-cyber-cyan/20' : 'bg-primary-50/70 border border-primary-200/50'
            }`}>
              <p className={`text-sm ${isNight ? 'text-gray-300' : 'text-primary-900'}`}>
                📧 {isNight ? 'Confirmation transmitted to' : 'A confirmation email with meeting details and a calendar invite has been sent to'} <strong className={isNight ? 'text-cyber-cyan' : ''}>{formData.email}</strong>
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => navigate('/')}
                className={`w-full ${isNight ? 'btn-neon' : 'btn-primary'}`}
              >
                {isNight ? 'RETURN HOME' : 'Return to Home'}
              </button>
              <button
                onClick={() => {
                  setStep(1)
                  setSelectedDate(null)
                  setSelectedTime(null)
                  setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    company: '',
                    service: '',
                    notes: '',
                  })
                }}
                className={`w-full px-6 py-3 rounded-lg font-medium transition-all ${
                  isNight
                    ? 'bg-void-elevated border border-cyber-cyan/20 text-gray-300 hover:border-cyber-cyan/50'
                    : 'bg-white/50 border border-gray-200/50 text-gray-700 hover:bg-white/70'
                }`}
              >
                {isNight ? 'SCHEDULE ANOTHER' : 'Schedule Another Appointment'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default BookAppointment
